import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Play, X, Volume2 } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  overline: { es: "Testimonios", en: "Testimonials" },
  title: {
    es: "Lo que dicen quienes nos eligieron",
    en: "What our students and partners say",
  },
  subtitle: {
    es: "Voces reales de profesionales y socios que han formado parte de nuestra trayectoria.",
    en: "Real voices from professionals and partners who have been part of our journey.",
  },
  watch: { es: "Ver testimonio", en: "Watch testimonial" },
  close: { es: "Cerrar", en: "Close" },
  unmute: { es: "Activar sonido", en: "Unmute" },
};

// Real video testimonials. Each entry is shared ES/EN since speakers talk in Spanish.
const VIDEOS = [
  {
    id: "carlos-alban",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/biqojrxz_Carlos%20Alba%CC%81n%20Factor%20Meetings.mp4",
    name: "Carlos Albán",
    role: { es: "Factor Meetings", en: "Factor Meetings" },
  },
  {
    id: "eleonora-garcia",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/drxntrb4_Eleonara%20Garci%CC%81a%20IBTM.mp4",
    name: "Eleonora García",
    role: { es: "IBTM", en: "IBTM" },
  },
  {
    id: "yazmin-vargas",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/n2b6x6g2_Formato%20vertical%20Yazmi%CC%81n%20Vargas.mp4",
    name: "Yazmín Vargas",
    role: { es: "Periodista especializada en Industria de Reuniones", en: "Journalist specialized in the Meetings Industry" },
  },
  {
    id: "carlos-chavez",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/n51yw6bx_Carlos%20Cha%CC%81vez.mp4",
    name: "Juan Carlos Chávez",
    role: { es: "Editor, Factor Meetings", en: "Editor, Factor Meetings" },
  },
  {
    id: "diplomado",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/7h9ke68h_Diplomado%20testimonio.mp4",
    name: "Francisco Moreno",
    role: { es: "Director de Turismo, Ciudad Juárez", en: "Tourism Director, Ciudad Juárez" },
  },
  {
    id: "chihuahua",
    src: "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/a61nq42q_Chihuaha%20Video.mp4",
    name: "Ivonne Barriga",
    role: { es: "Directora Comercial, Tour Integral", en: "Commercial Director, Tour Integral" },
  },
];

const VideoCard = ({ video, onPlay, index, isInView }) => {
  const { t, language } = useLanguage();
  const videoRef = useRef(null);

  // Lazy attempt at autoplaying preview muted on hover (graceful fail in mobile)
  const handleMouseEnter = () => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };
  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  };

  const displayName = typeof video.name === "string" ? video.name : t(video.name);

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onClick={() => onPlay(video)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`testimonial-card-${video.id}`}
      aria-label={`${t(translations.watch)} — ${displayName}`}
      className="group relative w-full aspect-[9/16] md:aspect-[3/4] rounded-2xl overflow-hidden bg-[#0B1F3B] border border-white/10 hover:border-[#F59E0B]/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#F59E0B]/15 text-left"
    >
      <video
        ref={videoRef}
        src={`${video.src}#t=0.5`}
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B] via-[#0B1F3B]/40 to-transparent" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#F59E0B] flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:bg-[#D97706] transition-all duration-300">
          <Play className="w-7 h-7 md:w-8 md:h-8 text-[#0B1F3B] ml-1" fill="currentColor" />
        </div>
      </div>

      {/* Caption */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-left">
        <h3 className="font-display text-white text-lg md:text-xl font-semibold leading-tight mb-1">
          {displayName}
        </h3>
        <p className="font-body text-[#2ED3C6] text-sm">{t(video.role)}</p>
      </div>

      {/* Watch indicator */}
      <div className="absolute top-4 right-4 px-3 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white/80 text-[10px] font-body uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {t(translations.watch)}
      </div>
    </motion.button>
  );
};

const VideoLightbox = ({ video, onClose }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!video) return;
    document.body.style.overflow = "hidden";
    const handleEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEsc);
    };
  }, [video, onClose]);

  if (!video) return null;
  const displayName = typeof video.name === "string" ? video.name : t(video.name);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        data-testid="video-lightbox"
        className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md md:max-w-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label={t(translations.close)}
            className="absolute -top-12 right-0 md:top-2 md:-right-12 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-all hover:rotate-90 duration-300 z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative aspect-[9/16] md:aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl">
            <video
              ref={videoRef}
              src={video.src}
              autoPlay
              controls
              playsInline
              muted={isMuted}
              className="w-full h-full"
            />
            {isMuted && (
              <button
                type="button"
                onClick={() => setIsMuted(false)}
                className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-2 bg-[#F59E0B] text-[#0B1F3B] rounded-full text-xs font-semibold font-body shadow-lg hover:bg-[#D97706] transition-colors"
              >
                <Volume2 className="w-3.5 h-3.5" />
                {t(translations.unmute)}
              </button>
            )}
          </div>

          <div className="text-center mt-5">
            <h3 className="font-display text-white text-xl md:text-2xl font-semibold">
              {displayName}
            </h3>
            <p className="font-body text-[#2ED3C6] text-sm mt-1">{t(video.role)}</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <section
      id="testimonios"
      data-testid="testimonials-section"
      ref={ref}
      className="py-24 md:py-32 relative overflow-hidden bg-[#0B1F3B]"
    >
      {/* Decorative gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F59E0B]/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#2ED3C6]/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-20 max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[#2ED3C6] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2
            data-testid="testimonials-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight mb-5"
          >
            {t(translations.title)}
          </h2>
          <p className="font-body text-white/65 text-base md:text-lg leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        {/* Video grid: 2 cols mobile, 3 cols tablet+, 6 videos in 2 balanced rows */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
          {VIDEOS.map((video, i) => (
            <VideoCard
              key={video.id}
              video={video}
              index={i}
              isInView={isInView}
              onPlay={setActive}
            />
          ))}
        </div>
      </div>

      <VideoLightbox video={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default TestimonialsSection;
